import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  PERMIT_ADDONS,
  calculateAddOnPrice,
} from "../data/staticData";

const AddOnsCtx = createContext(null);
const STORAGE_KEY = "fld_permit_addons";

const AVAILABLE_CODES = new Set(
  PERMIT_ADDONS.filter((a) => a.available).map((a) => a.code)
);

const sanitize = (codes) =>
  Array.from(new Set(codes))
    .map((c) => String(c).trim().toUpperCase())
    .filter((c) => AVAILABLE_CODES.has(c));

export function AddOnsProvider({ children }) {
  const [selected, setSelected] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? sanitize(JSON.parse(saved)) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(selected));
  }, [selected]);

  const toggle = useCallback((code) => {
    setSelected((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  }, []);

  const setAll = useCallback((codes) => {
    setSelected(sanitize(codes));
  }, []);

  const clear = useCallback(() => setSelected([]), []);

  const pricing = useMemo(() => calculateAddOnPrice(selected), [selected]);

  const value = useMemo(
    () => ({ selected, toggle, setAll, clear, pricing }),
    [selected, toggle, setAll, clear, pricing]
  );

  return <AddOnsCtx.Provider value={value}>{children}</AddOnsCtx.Provider>;
}

export const useAddOns = () => {
  const ctx = useContext(AddOnsCtx);
  if (!ctx) throw new Error("useAddOns must be inside AddOnsProvider");
  return ctx;
};
